import { cn } from '@trafiflow/shared-ui'
import { Link2, Image, Video, Sparkles, Check, X, AlertTriangle } from 'lucide-react'

interface Capability {
  key: string
  label: string
  available: boolean
  degraded?: boolean
  detail?: string
}

interface CapabilityBadgesProps {
  capabilities: Capability[]
  className?: string
}

const CAPABILITY_ICONS: Record<string, typeof Link2> = {
  meta_connected: Link2,
  ads_library: Image,
  footage: Video,
  ai_analysis: Sparkles,
}

export default function CapabilityBadges({ capabilities, className }: CapabilityBadgesProps) {
  if (!capabilities || capabilities.length === 0) return null

  return (
    <div className={cn('flex flex-wrap gap-2', className)}>
      {capabilities.map((cap) => {
        const Icon = CAPABILITY_ICONS[cap.key] || Sparkles
        const StatusIcon = cap.available ? Check : cap.degraded ? AlertTriangle : X

        return (
          <div
            key={cap.key}
            className={cn(
              'flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium transition-all',
              cap.available && 'bg-emerald-500/10 text-emerald-500 border border-emerald-500/20',
              cap.degraded && 'bg-amber-500/10 text-amber-500 border border-amber-500/20',
              !cap.available && !cap.degraded && 'bg-tf-bg-muted text-tf-text-muted border border-tf-border',
            )}
            title={cap.detail}
          >
            <Icon className="w-3.5 h-3.5" />
            <span>{cap.label}</span>
            <StatusIcon className="w-3 h-3" />
            {cap.detail && (
              <span className="text-[10px] opacity-70">({cap.detail})</span>
            )}
          </div>
        )
      })}
    </div>
  )
}

// Helper to parse capabilities from preflight artifact
export function parsePreflightCapabilities(artifact: Record<string, unknown> | undefined): Capability[] {
  if (!artifact) return []

  const caps: Capability[] = []

  // Meta connection
  const metaConnected = artifact.meta_connected as boolean | undefined
  caps.push({
    key: 'meta_connected',
    label: 'Meta',
    available: !!metaConnected,
    detail: metaConnected ? 'Connected' : 'Not connected',
  })

  // Ads library
  const adsAvailable = artifact.ads_available as boolean | undefined
  const adCount = artifact.ad_count as number | undefined
  caps.push({
    key: 'ads_library',
    label: 'Ads',
    available: !!adsAvailable,
    detail: adCount ? `${adCount} found` : undefined,
  })

  // Footage
  const footageAvailable = artifact.footage_available as boolean | undefined
  const clipCount = artifact.clip_count as number | undefined
  caps.push({
    key: 'footage',
    label: 'Footage',
    available: !!footageAvailable,
    detail: clipCount ? `${clipCount} clips` : undefined,
  })

  // AI Analysis
  const aiAvailable = artifact.ai_available as boolean | undefined
  caps.push({
    key: 'ai_analysis',
    label: 'AI',
    available: aiAvailable !== false, // Default to true
    degraded: aiAvailable === false,
    detail: aiAvailable !== false ? 'Ready' : 'Limited',
  })

  return caps
}
